"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.XlsxExtraction = void 0;
const common_1 = require("@finley-technologies/common");
const tape_sanitizer_1 = require("@finley-technologies/tape-sanitizer");
const _1 = require(".");
const typeguards_1 = require("../typeguards");
const findDirectDependencies = (graph, artifactId, configPredicate) => {
    return graph.dependencies
        .filter(({ inputArtifactId }) => inputArtifactId === artifactId)
        .map(({ outputArtifactId }) => {
        const outputArtifact = graph.artifacts.find(({ id }) => id === outputArtifactId);
        if (!outputArtifact || !configPredicate(outputArtifact.configuration)) {
            return null;
        }
        // This redundant object spread converts ArtifactWithSlugs to TypedArtifact.
        return { ...outputArtifact, configuration: outputArtifact.configuration };
    })
        .filter(common_1.typeguards.isNotNullOrUndefined);
};
// TODO(jon): rename this file - it's not xlsx specific anymore.
var XlsxExtraction;
(function (XlsxExtraction) {
    const HEADER_RANGE_REGEX = /^[A-Za-z]+(?<startRow>[0-9]+):[A-Za-z]+(?<endRow>[0-9]+)$/;
    const toXlsxRowNumber = (xlsxConfig, csvRowNumber) => {
        const headerRange = xlsxConfig.mode === 'convertRange' && xlsxConfig.headerRange;
        if (!headerRange)
            return csvRowNumber;
        const found = headerRange.match(HEADER_RANGE_REGEX);
        (0, common_1.finleyAssert)(found?.groups, `Invalid header range [${headerRange}]`);
        const { startRow, endRow } = found.groups;
        (0, common_1.finleyAssert)(startRow === endRow, `Invalid header range [${headerRange}]`);
        // The first row of a CSV is headers, so `csvRowNumber - 1` is the number of rows past the header.
        // To map to XLSX row number, add the row number of the header.
        return csvRowNumber - 1 + Number.parseInt(startRow, 10);
    };
    const collectXlsxErrors = (pipelineError, xlsxConfig, getFieldInfo, output) => {
        if (pipelineError.type !== 'transformError')
            return;
        const transformError = pipelineError.content;
        switch (transformError.type) {
            case 'xlsx2Csv': {
                const { message, sheetName, fieldName, cell } = transformError;
                output.xlsxExtractionErrors.push({
                    message,
                    sheetName,
                    fieldName,
                    cell,
                });
                break;
            }
            case 'fieldValidation':
                transformError.invalidFields.forEach((invalidField) => {
                    const { sheetName, cell, columnInfo: fieldInfo } = getFieldInfo(invalidField.name);
                    output.xlsxCellErrors.push({
                        sheetName,
                        fieldInfo,
                        cell,
                        // If we have an exact cell address, the row number is not useful. It might even be
                        // misleading in the case of `extractValues`, because the row number will always be 2.
                        rowNumber: cell
                            ? undefined
                            : toXlsxRowNumber(xlsxConfig, transformError.row),
                        enteredValue: invalidField.value,
                    });
                });
                break;
            case 'columnValidation': {
                const { sheetName, columnInfo: fieldInfo } = getFieldInfo(transformError.field.name);
                output.xlsxColumnErrors.push({ sheetName, fieldInfo });
                break;
            }
            default:
                (0, common_1.finleyAssertUnreachable)(transformError);
        }
    };
    const collectCsvErrors = (pipelineError, getFieldInfo, output) => {
        if (pipelineError.type !== 'transformError')
            return;
        const transformError = pipelineError.content;
        switch (transformError.type) {
            case 'xlsx2Csv':
                break; // should be impossible, but safe to ignore
            case 'fieldValidation': {
                transformError.invalidFields.forEach((invalidField) => {
                    const fieldInfo = getFieldInfo(invalidField.name);
                    output.csvFieldErrors.push({
                        fieldInfo,
                        enteredValue: invalidField.value,
                        rowNumber: transformError.row,
                    });
                });
                break;
            }
            case 'columnValidation': {
                const fieldInfo = getFieldInfo(transformError.field.name);
                output.csvColumnErrors.push({ fieldInfo });
                break;
            }
            default:
                (0, common_1.finleyAssertUnreachable)(transformError);
        }
    };
    const mapBySanitizedFieldName = (columns, sanitizeFieldNames) => new Map(columns.map((column) => [
        (0, tape_sanitizer_1.getSanitizedFieldName)(column.name, sanitizeFieldNames),
        column,
    ]));
    const getFieldGroup = ({ xlsx2CsvArtifact, sanitizeArtifact, }) => {
        const xlsxConfig = xlsx2CsvArtifact?.configuration;
        const sanitizeConfig = sanitizeArtifact.configuration;
        // `sanitizeConfig.baseFields` and the `ColumnInfo`s we derive from it use the original field
        // name formatting from the tape, which is clearest to present to the user. Elsewhere, we may
        // use reformatted ("sanitized") field names. For simplicity, always sanitize before comparing
        // field names, even when technically not necessary.
        const sanitizeFieldNames = !sanitizeConfig.doNotSanitizeFieldNames;
        switch (xlsxConfig?.mode) {
            case 'extractValues': {
                const columns = xlsxConfig.extract
                    .map(({ sheetName, cellAddress, fieldName }) => {
                    const sanitizeField = sanitizeConfig.baseFields.find(({ field }) => 
                    // Although it is a best practice for the xlsxConfig and sanitizeConfig field names
                    // to match exactly, this is not enforced. To be safe, sanitize both names before
                    // performing comparison.
                    (0, tape_sanitizer_1.getSanitizedFieldName)(field, sanitizeFieldNames) ===
                        (0, tape_sanitizer_1.getSanitizedFieldName)(fieldName, sanitizeFieldNames));
                    return sanitizeField
                        ? { ...(0, tape_sanitizer_1.toColumnInfo)(sanitizeField), sheetName, cellAddress }
                        : null;
                })
                    .filter(common_1.typeguards.isNotNullOrUndefined);
                const columnMap = mapBySanitizedFieldName(columns, sanitizeFieldNames);
                const getFieldInfo = (fieldName) => {
                    const columnInfo = columnMap.get((0, tape_sanitizer_1.getSanitizedFieldName)(fieldName, sanitizeFieldNames));
                    (0, common_1.finleyAssert)(columnInfo, `field name [${fieldName}] not found`);
                    return {
                        sheetName: columnInfo.sheetName,
                        cell: columnInfo.cellAddress,
                        columnInfo,
                    };
                };
                return {
                    mode: 'extractValues',
                    dataTapeSlug: sanitizeArtifact.artifactSlug,
                    columns,
                    xlsx2CsvArtifact: common_1.utils.ensure(xlsx2CsvArtifact),
                    sanitizeArtifact,
                    collectErrors: (error, output) => collectXlsxErrors(error, xlsxConfig, getFieldInfo, output),
                };
            }
            case 'convertRange': {
                const { sheetName, headerRange } = xlsxConfig;
                const columns = sanitizeConfig.baseFields.map(tape_sanitizer_1.toColumnInfo);
                const columnMap = mapBySanitizedFieldName(columns, sanitizeFieldNames);
                const getFieldInfo = (fieldName) => {
                    const columnInfo = columnMap.get((0, tape_sanitizer_1.getSanitizedFieldName)(fieldName, sanitizeFieldNames));
                    (0, common_1.finleyAssert)(columnInfo, `field name [${fieldName}] not found`);
                    // Since we only have a header range, we don't know precisely which cell corresponds to a
                    // given field name.
                    return { sheetName, cell: undefined, columnInfo };
                };
                return {
                    mode: 'extractTape',
                    dataTapeSlug: sanitizeArtifact.artifactSlug,
                    sheetName,
                    headerRange,
                    columns,
                    xlsx2CsvArtifact: common_1.utils.ensure(xlsx2CsvArtifact),
                    sanitizeArtifact,
                    collectErrors: (error, output) => collectXlsxErrors(error, xlsxConfig, getFieldInfo, output),
                };
            }
            case undefined: {
                const columns = sanitizeConfig.baseFields.map(tape_sanitizer_1.toColumnInfo);
                const columnMap = mapBySanitizedFieldName(columns, sanitizeFieldNames);
                const getFieldInfo = (fieldName) => {
                    const columnInfo = columnMap.get((0, tape_sanitizer_1.getSanitizedFieldName)(fieldName, sanitizeFieldNames));
                    (0, common_1.finleyAssert)(columnInfo, `field name [${fieldName}] not found`);
                    return columnInfo;
                };
                return {
                    mode: 'csv',
                    dataTapeSlug: sanitizeArtifact.artifactSlug,
                    columns,
                    xlsx2CsvArtifact: null,
                    sanitizeArtifact,
                    collectErrors: (error, output) => collectCsvErrors(error, getFieldInfo, output),
                };
            }
            default:
                throw (0, common_1.finleyAssertUnreachable)(xlsxConfig);
        }
    };
    /**
     * Returns groups of fields extracted from an xlsx.
     *
     * The list is pre-sorted in a reasonable display order.
     *
     * If an empty list is returned, `rawArtifactId` is not configured for xlsx extraction. In other
     * words, it can be assumed that the raw artifact is not an xlsx file.
     */
    XlsxExtraction.getXlsxFieldGroups = (graph, rawArtifactId) => {
        const xlsxArtifacts = findDirectDependencies(graph, rawArtifactId, typeguards_1.typeguards.isTapeBuilderXlsx2CsvTask);
        return xlsxArtifacts
            .flatMap((xlsx2CsvArtifact) => {
            const sanitizeArtifacts = findDirectDependencies(graph, xlsx2CsvArtifact.id, typeguards_1.typeguards.isTapeBuilderSanitizeTask);
            return sanitizeArtifacts.map((sanitizeArtifact) => getFieldGroup({ xlsx2CsvArtifact, sanitizeArtifact }));
        })
            .sort(({ xlsx2CsvArtifact: xlsxA, sanitizeArtifact: sanitizeA }, { xlsx2CsvArtifact: xlsxB, sanitizeArtifact: sanitizeB }) => {
            (0, common_1.finleyAssert)(xlsxA && xlsxB, 'expected xlsx field group');
            // For each xlsx, show extractValues ahead of extractTape.
            if (xlsxA.configuration.mode !== xlsxB.configuration.mode)
                return xlsxA.configuration.mode === 'extractValues' ? -1 : 1;
            // Order doesn't matter, as long as it's consistent.
            return sanitizeA.artifactSlug.localeCompare(sanitizeB.artifactSlug);
        });
    };
    XlsxExtraction.getCsvFieldGroups = (graph, rawArtifactId) => {
        const dependantIds = _1.BbplGraph.getDependants(graph, rawArtifactId);
        // In the most common case, we have
        // raw node -> sanitize node
        //
        // however, in some cases we have
        // raw node -> prefilter node -> prefilter node ... -> sanitize node
        //
        // Since prefilter only filters out rows, but does not change the field definitions, it's
        // correct to say that the sanitize node describe the raw tape, even when there are prefilter
        // nodes between them.
        const artifactsReachableByPrefilter = _1.BbplGraph.traverseBreadthFirst(graph, dependantIds, ({ artifacts }, artifactId) => {
            const artifact = artifacts.find(({ id }) => id === artifactId);
            if (typeguards_1.typeguards.isTapeBuilderPreFilterTask(artifact?.configuration)) {
                return _1.BbplGraph.getDependants(graph, artifactId);
            }
            return [];
        });
        return artifactsReachableByPrefilter
            .map((artifact) => {
            if (typeguards_1.typeguards.isTapeBuilderSanitizeTask(artifact.configuration)) {
                // This redundant object spread converts ArtifactWithSlugs to TypedArtifact.
                const sanitizeArtifact = {
                    ...artifact,
                    configuration: artifact.configuration,
                };
                return getFieldGroup({ xlsx2CsvArtifact: null, sanitizeArtifact });
            }
            return null;
        })
            .filter(common_1.typeguards.isNotNullOrUndefined)
            .sort(({ sanitizeArtifact: sanitizeA }, { sanitizeArtifact: sanitizeB }) => sanitizeA.artifactSlug.localeCompare(sanitizeB.artifactSlug));
    };
})(XlsxExtraction = exports.XlsxExtraction || (exports.XlsxExtraction = {}));
//# sourceMappingURL=xlsx.js.map